import * as moment from 'moment'
import * as last from 'lodash/last'
import { Item, Dot, LineType } from 'components/features/pfm/LineChart/LineChart'
import { DateXMap } from 'helpers/trendHelpers'
import { TrendLine, Trend } from 'interfaces/trend'
import { DATE_FORMAT_ISO_FULL } from 'constants/datetime'
import * as isNumber from 'lodash/isNumber'
import { BaseTransaction, Transaction, FullMoneyflowType } from 'interfaces/moneyflow'
import { Timeframe } from 'interfaces/chart'
import { HISTORICAL_TIMEFRAMES } from 'constants/datetime'
import { TypedMoneyflowCategory } from 'interfaces/meta'
import { Collection } from 'interfaces'
import { ChangeTransactionCategory } from 'actions/moneyflowActions'
import { isRequestAction, isFailAction, isSuccessAction } from 'helpers/actionsHelpers'
import { getItemsByKey, concatCollections, setItemInCollection, normalize } from 'helpers/normalizeHelpers'

// TODO - combine these with trendHelpers

// TRENDS HELPERS
export const buildMemberItems = (trendLine: TrendLine, xMap: DateXMap): Item[] => {
  return trendLine.reduce((items, point) => {
    const prevItem = last(items)
    const prevSum = prevItem ? prevItem.y : 0

    const item: Item = {
      x: xMap[point.date],
      y: prevSum + point.amount,
    }

    items.push(item)
    return items
  }, [] as Item[])
}

export const buildMemberProjectedItems = (trend: Trend, xMap: DateXMap): Item[] => {
  const { actual, projected } = trend

  if (!projected || projected.length < 2) {
    return []
  }

  const actualTotal = actual.reduce((result, point) => result + point.amount, 0)
  const lastActual = last(actual)
  const lastActualAmount = lastActual.amount
  const [firstProjected, ...restProjected] = projected
  const projectedAmend = Math.max(firstProjected.amount - lastActualAmount, 0)
  const initialProjected = actualTotal + projectedAmend

  const firstItem: Item = {
    x: xMap[lastActual.date],
    y: actualTotal,
  }

  const restItems = restProjected.reduce((items, point) => {
    const prevAmount = items.length ? last(items).y : initialProjected
    const item: Item = {
      x: xMap[point.date],
      y: prevAmount + point.amount,
    }

    items.push(item)
    return items
  }, [] as Item[])

  return [firstItem, ...restItems]
}

/**
 * Build trend points for daily moneyflow line chart
 * @param trend A whole trend
 * @param month Current month
 * @param today Pass null if now isn't current month
 * @param xMap Map 'day date' -> 'x coordinate'
 */
export const buildPeerPoints = (trend: Trend, month: string, today: string | null, xMap: DateXMap): Record<'actual' | 'projected', Item[]> => {
  const { actual, projected } = trend
  const actualTrendItem = actual.find((item) => item.date === month)
  if (!actualTrendItem) {
    throw new Error(`Peers trend doesn't have actual data for month ${ month }`)
  }
  const projectedTrendItem = projected ? projected.find((item) => item.date === month) : null

  const monthDate = moment(month)
  const daysCount = monthDate.daysInMonth()
  const firstX = xMap[monthDate.startOf('month').format(DATE_FORMAT_ISO_FULL)]
  const lastX = xMap[monthDate.endOf('month').format(DATE_FORMAT_ISO_FULL)]
  const todayX = today ? xMap[today] : null
  const actualDaysCount = today ? moment(today).date() : daysCount

  const firstActualValue = Math.round(actualTrendItem.amount / actualDaysCount)
  const currentX = isNumber(todayX) ? todayX : lastX
  const currentValue = Math.round(actualTrendItem.amount)
  const actualPoints = [
    { x: firstX, y: firstActualValue },
    { x: currentX, y: currentValue },
  ]

  // if have last day of current month we don't show projected even exists
  const hasProjected = Boolean(today) && lastX !== todayX
  const projectedPoints = hasProjected && projectedTrendItem
    ? [
      { x: currentX, y: currentValue },
      { x: lastX, y: Math.round(projectedTrendItem.amount) },
    ]
    : []

  return {
    actual: actualPoints,
    projected: projectedPoints,
  }
}

const itemToDot = (item: Item, type: LineType): Dot => ({
  x: item.x,
  y: item.y,
  type,
})

export const buildTrendDots = (actual: Item[], projected: Item[], type: LineType): Dot[] => {
  const dots: Dot[] = [
    itemToDot(last(actual), type),
  ]

  if (projected.length > 1) {
    dots.push(
      itemToDot(last(projected), type)
    )
  }

  return dots
}

export const isTransactionEditable = (transaction: BaseTransaction) =>
  transaction.account_source_type === 'yodlee' && transaction.status !== 'pending'

export const isStatusCardTransaction = (transaction: BaseTransaction) =>
  transaction.account_source_type === 'railsbank'

export const isTimeframeHistorical = (timeframe: Timeframe) =>
  HISTORICAL_TIMEFRAMES.includes(timeframe)

export const updateTransactionCategory = (prevTransaction: Transaction, nextCategory: TypedMoneyflowCategory): Transaction => {
  const { category: prevCategory, amount } = prevTransaction

  if (prevCategory.id === nextCategory.id) {
    return prevTransaction
  }

  const differentCategoryType = prevCategory.type !== null
    && nextCategory.type !== null
    && prevCategory.type !== nextCategory.type

  const amountMulti = differentCategoryType ? -1 : 1

  return {
    ...prevTransaction,
    category: nextCategory,
    // change a sign of amount if `spending` transaction become `income` and vice versa
    amount: amountMulti * amount,
  }
}

type Arg = {
  action: ChangeTransactionCategory
  type: FullMoneyflowType | null
  prevCollection: Collection<Transaction, 'uid'>
}
export const processChangeTransactionCategory = (arg: Arg): Collection<Transaction, 'uid'> => {
  const { action, type, prevCollection } = arg

  const {
    type: flowType,
    category: nextCategory,
    transaction: prevTransaction,
    apply_to_ids,
  } = action.payload.params

  if (flowType !== null && type !== flowType) {
    return prevCollection
  }

  let nextCollection = prevCollection
  const isRequest = isRequestAction(action)

  if (isRequest || isFailAction(action)) {
    const updatedCategory = isRequest ? nextCategory : prevTransaction.category

    const nextTransactions = [
      prevTransaction,
      ...getItemsByKey<Transaction>(prevCollection.byKey, apply_to_ids),
    ].map((transaction) => updateTransactionCategory(transaction, updatedCategory))

    nextCollection = concatCollections(nextCollection, normalize(nextTransactions, 'uid'))
  }

  if (isSuccessAction(action)) {
    const nextTransaction = action.payload.response

    nextCollection = setItemInCollection(nextCollection, nextTransaction, 'uid', { checkEquality: true })
  }

  return nextCollection
}
